import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SITE } from "../data/site";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  useReveal();
  usePageMeta({ title: "Contact Us", description: "Get in touch with Thrive Bridge Mental Wellness CIC." });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact us"
        title="Contact Thrive Bridge"
        lead="Get in touch by phone, email or the form below. For referrals, please use the Get Support page."
      />

      <section className="container-page section-spacing grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5 fade-up">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--tb-text)] mb-6">Contact details</h2>
          <ul className="space-y-5 text-[var(--tb-text-2)]">
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Mail className="h-5 w-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--tb-muted)] font-semibold">Email</p>
                <a href={`mailto:${SITE.email}`} className="text-[var(--tb-text)] hover:text-[var(--tb-primary)] font-medium">{SITE.email}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Phone className="h-5 w-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--tb-muted)] font-semibold">Phone</p>
                <a href={`tel:${SITE.phoneRaw}`} className="text-[var(--tb-text)] hover:text-[var(--tb-primary)] font-medium">{SITE.phone}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><MapPin className="h-5 w-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--tb-muted)] font-semibold">Address</p>
                <p className="text-[var(--tb-text)] font-medium">{SITE.address}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Clock className="h-5 w-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--tb-muted)] font-semibold">Office hours</p>
                <p className="text-[var(--tb-text)] font-medium">{SITE.hours}</p>
              </div>
            </li>
          </ul>

          <div className="mt-8 rounded-3xl overflow-hidden border border-[var(--tb-border)] aspect-[16/10]">
            <iframe
              title="Office location map — 321-323 High Road, Chadwell Heath, Essex RM6 6AX"
              src="https://www.google.com/maps?q=321-323+High+Road,+Chadwell+Heath,+Essex+RM6+6AX,+UK&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        <div className="lg:col-span-7 fade-up">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[var(--tb-border)] rounded-3xl p-6 md:p-10 grid gap-5 md:grid-cols-2"
          >
            <Field label="Your name" required>
              <input required data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Your email" required>
              <input required type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Subject" required className="md:col-span-2">
              <input required data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Message" required className="md:col-span-2">
              <textarea required rows={6} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-y`} />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-[var(--tb-border)] bg-white px-4 py-3 text-[var(--tb-text)] placeholder:text-[var(--tb-muted)] focus:border-[var(--tb-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--tb-primary)]/20 transition-all";

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-[var(--tb-text)] mb-2">
        {label} {required && <span className="text-[var(--tb-crisis)]" aria-hidden="true">*</span>}
      </label>
      {children}
    </div>
  );
}
